import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Editorial primary CTA = filled `ink` background with cream label,
 * the "save / send / continue" surface across the app. The other
 * variants are intentionally restrained — `outline` for secondary
 * actions, `ghost` for tertiary, `danger` for destructive, `accent`
 * for the rare highlight (e.g. AI-generated CTA).
 */
const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium',
    'transition-[background,color,box-shadow,opacity] duration-150',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-50',
    'data-[loading=true]:pointer-events-none',
  ].join(' '),
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-primary-foreground hover:bg-primary/92 active:bg-primary/85 shadow-sm',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        outline:
          'border border-line-strong bg-transparent text-foreground hover:bg-secondary/60',
        ghost:
          'bg-transparent text-foreground hover:bg-secondary/60',
        accent:
          'bg-accent text-accent-foreground hover:bg-accent/90 shadow-sm',
        danger:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        link: 'bg-transparent text-foreground underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-10 px-4',
        lg: 'h-11 px-6 text-base',
        xl: 'h-12 px-8 text-base',
        icon: 'size-10',
        'icon-sm': 'size-8',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      className,
      variant,
      size,
      asChild,
      loading,
      leadingIcon,
      trailingIcon,
      children,
      disabled,
      type,
      ...rest
    },
    ref,
  ) {
    const Comp = asChild ? Slot : 'button';
    // Radix `Slot` (the `asChild` impl) passes everything through
    // `React.Children.only`, which rejects sibling slots even when
    // they're `undefined`/`false`. So when `asChild` is true we
    // must render exactly one child and let the caller compose the
    // icon themselves: `<Button asChild><Link><Plus/> New</Link></Button>`.
    // The loading + leading/trailing-icon affordances only apply to
    // the real `<button>` path.
    const content = asChild ? (
      children
    ) : (
      <>
        {loading ? <Loader2 className="size-4 animate-spin" /> : leadingIcon}
        {children}
        {!loading && trailingIcon}
      </>
    );
    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : type ?? 'button'}
        className={cn(buttonVariants({ variant, size }), className)}
        data-loading={loading || undefined}
        aria-busy={loading || undefined}
        disabled={disabled || loading}
        {...rest}
      >
        {content}
      </Comp>
    );
  },
);

export { buttonVariants };
